import React, { useEffect, useState } from 'react';
import './App.css';

function App() {
  const [status, setStatus] = useState<string>('Loading...');

  // Allowed domain patterns for security
  const ALLOWED_DOMAIN_PATTERNS = [
    /^https:\/\/choreograph\.os\.wpp\.com$/,
    /^https:\/\/[a-f0-9-]+\.apps\.wpp-stage\.os-dev\.io$/,
    /^https:\/\/ch\.wpp-stage\.os-dev\.io$/
  ];

  // Check if the request is coming from an allowed domain
  const isAllowedDomain = (): boolean => {
    const referrer = document.referrer;
    const opener = window.opener;
    
    console.log('[AMC AUTH PROXY] Checking domain - Referrer:', referrer, 'Has opener:', !!opener);
    
    // Check referrer
    if (referrer) {
      const referrerUrl = new URL(referrer);
      const referrerOrigin = `${referrerUrl.protocol}//${referrerUrl.host}`;
      console.log('[AMC AUTH PROXY] Referrer origin:', referrerOrigin);
      if (ALLOWED_DOMAIN_PATTERNS.some(pattern => pattern.test(referrerOrigin))) {
        console.log('[AMC AUTH PROXY] ✓ Referrer matched allowed pattern');
        return true;
      }
    }
    
    // Check opener origin if available
    if (opener) {
      try {
        const openerOrigin = opener.location.origin;
        console.log('[AMC AUTH PROXY] Opener origin:', openerOrigin);
        if (ALLOWED_DOMAIN_PATTERNS.some(pattern => pattern.test(openerOrigin))) {
          console.log('[AMC AUTH PROXY] ✓ Opener matched allowed pattern');
          return true;
        }
      } catch (e) {
        // Cross-origin restriction - can't access opener.location
        // If we have an opener but can't access it due to CORS, assume it's valid
        // since the firewall already restricts access to Cloudflare IPs only
        console.log('[AMC AUTH PROXY] Cross-origin opener detected, allowing due to firewall protection');
        return true;
      }
    }
    
    console.log('[AMC AUTH PROXY] ✗ No valid referrer or opener found');
    return false;
  };

  // Helper function to send auth code to parent window
  const sendCodeToParent = (authCode: string): void => {
    if (window.opener && !window.opener.closed) {
      try {
        // Determine the target origin from referrer or use allowed domains
        const referrer = document.referrer;
        let targetOrigin = '*';
        
        if (referrer) {
          const referrerUrl = new URL(referrer);
          const referrerOrigin = `${referrerUrl.protocol}//${referrerUrl.host}`;
          if (ALLOWED_DOMAIN_PATTERNS.some(pattern => pattern.test(referrerOrigin))) {
            targetOrigin = referrerOrigin;
          }
        }

        window.opener.postMessage(
          {
            authCode: authCode,
            source: 'amazon-auth-bridge',
            timestamp: Date.now(),
          },
          targetOrigin
        );
        // console.log('[AMC AUTH PROXY] Sent postMessage to parent');
        setStatus('Success! Closing window...');

        // Close window after short delay
        setTimeout(() => {
        //   window.close();
        }, 1500);
      } catch (e) {
        console.error('[AMC AUTH PROXY] Failed to send postMessage:', e);
        // Fallback to localStorage
        localStorage.setItem(
          'amazonAuthData',
          JSON.stringify({
            authCode: authCode,
            timestamp: Date.now(),
          })
        );
        setTimeout(() => {
        //   window.close();
        }, 2000);
      }
    } else {
      // console.log('[AMC AUTH PROXY] No opener, using localStorage fallback');
      localStorage.setItem(
        'amazonAuthData',
        JSON.stringify({
          authCode: authCode,
          timestamp: Date.now(),
        })
      );
      setTimeout(() => {
        // window.close();
      }, 2000);
    }
  };

  useEffect(() => {
    console.log('[AMC AUTH PROXY] Page loaded');
    console.log('[AMC AUTH PROXY] URL:', window.location.href);
    console.log('[AMC AUTH PROXY] Referrer:', document.referrer);
    
    // Security check: Verify the request is from an allowed domain
    if (!isAllowedDomain()) {
      console.error('[AMC AUTH PROXY] Access denied: Request not from allowed domain');
      setStatus('Access denied: Unauthorized domain');
      return;
    }
    
    // console.log('[AMC AUTH PROXY] Domain check passed');
    // console.log('[AMC AUTH PROXY] Full search params:', window.location.search);

    const urlParams = new URLSearchParams(window.location.search);
    const clientId = urlParams.get('clientId');
    const authCode = urlParams.get('code');
    const error = urlParams.get('error');
    const scope = urlParams.get('scope');
    
    // console.log('[AMC AUTH PROXY] URL Params:', {
    //   clientId: clientId,
    //   authCode: authCode,
    //   error: error,
    //   scope: scope,
    //   allParams: Array.from(urlParams.entries())
    // });

    // Step 1: If we have clientId, redirect to Amazon OAuth directly
    if (clientId && !authCode && !error) {
      // console.log('[AMC AUTH PROXY] Client ID received:', clientId);
      setStatus('Redirecting to Amazon...');

      // Build the Amazon OAuth URL for Login with Amazon (LWA)
      const redirectUri = encodeURIComponent(window.location.origin);
      const scopeParam = encodeURIComponent('profile advertising::campaign_management advertising::audiences');
      const decodedClientId = decodeURIComponent(clientId);
      
      // Use the proper LWA endpoint (na.account.amazon.com for North America)
      const amazonAuthUrl = `https://na.account.amazon.com/ap/oa?client_id=${decodedClientId}&scope=${scopeParam}&response_type=code&redirect_uri=${redirectUri}`;
      
      // console.log('[AMC AUTH PROXY] Redirecting to:', amazonAuthUrl);
      
      // Redirect to Amazon OAuth
      setTimeout(() => {
        window.location.href = amazonAuthUrl;
      }, 500);

      return;
    }

    // Step 2: If we have an auth code (from Amazon redirect), send it back to parent
    if (authCode) {
      // console.log('[AMC AUTH PROXY] ✅ Received auth code from Amazon:', authCode);
      setStatus('Authentication successful!');
      sendCodeToParent(authCode);
      return;
    }

    // Step 3: Handle errors
    if (error) {
      console.error('[AMC AUTH PROXY] ❌ Amazon auth error:', error);
      setStatus('Authentication failed');

      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          {
            error: error,
            source: 'amazon-auth-bridge',
            timestamp: Date.now(),
          },
          '*'
        );
      }

      setTimeout(() => {
        // window.close();
      }, 3000);
      return;
    }

    // Step 4: If we're back from Amazon but don't have code or error yet, wait
    // This handles intermediate redirects during the OAuth flow
    if (!clientId && !authCode && !error) {
      // console.log('[AMC AUTH PROXY] ⏳ Waiting for Amazon OAuth flow to complete...');
      setStatus('Please complete the authorization.');
      // Don't close the window - let Amazon continue the OAuth flow
    }
  }, []);

  return (
    <div className="container">
      <h2>AMC Authentication</h2>
      <div className="spinner"></div>
      <p>{status}</p>
      <small>This window will close automatically</small>
    </div>
  );
}

export default App;
